/**
 * Générateur — Automatismes : résoudre des équations du type ax = c et
 * x + b = c (1225) ; du type ax + b = c (1226).
 * Attendu ID : 1225 | Niveau : 3ème | Domaine : Calcul littéral et algébrique
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 11, ligne 794).
 * UN FICHIER, DEUX ATTENDUS (base du prof du 13/09, modèle 1213/1246) : les
 * clés simple_* portent l'attendu 1225 (une étape, relatifs), les clés
 * etapes_* l'attendu 1226 (deux étapes) ; gen_1226 est un dispatch sur les
 * clés etapes_* (genererParmi).
 *
 * ── Périmètre (cadrage programme-maths 2026-09-12, mode auto) ─────────────
 * EXTENSION de gen_1107 (4e : x + b = c et ax = c, positifs, une ligne
 * décimale, fraction irréductible) : ses tireurs sont importés pour
 * l'entretien ; le NEUF de 3e est (1) les RELATIFS (x + 8 = 3 → −5 ;
 * 3x = −12 → −4 : objectifs 4e l. 640-643 acquis) et (2) la DEUXIÈME ÉTAPE
 * ax + b = c (mise en œuvre Eduscol 2026 p. 18 « ax + b = c, entiers
 * relatifs »). Formes nommées = bornes : x + b, b + x, ax, ax + b, b + ax,
 * ax − b ; exclus : x − b, b − x, ax + b = cx + d (objectif 3e l. 806,
 * gen_211), coefficients fractions (1213). Choix mode auto, À ARBITRER :
 * a toujours positif (DEPP 2de 2025 q.6 « −2x = 1 » : 44,4 %), c ≠ 0, la
 * fraction reste dans ax = c (importée de 1107), (3 ; 12) sacrifié dans
 * ax = c (c'est l'étape de la fiche).
 * Banque MathsMentales (S. Cogez, Apache 2.0, relevé 2026-09-12) : 4NE6/7/8
 * (a, b, c entiers −10..10 ou −99..99), 4NE9 option 1 « ax + b = c »
 * (a, b, c −10..10) ; retenu : x entier −6..6 non nul en deux étapes, b 1..9,
 * a 2..9.
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   1225, carte 0 « Étape par étape » (simple_guide_texte_trou_saisie) : la
 *     résolution écrite, l'élève remplit les nombres (x + 8 − ⬚ = 3 − ⬚, x = ⬚ ;
 *     3x ÷ ⬚ = −12 ÷ ⬚, x = ⬚), demande prof 13/09 « beaucoup plus guidé ».
 *   1225, carte 1 « Série flash » (simple_flash_texte_trou_saisie) : trois
 *     lignes KaTeX « équation, x = … » : x + b = c (relatifs, solution
 *     négative une fois sur quatre), ax = c entier (c négatif une fois sur
 *     trois), ax = c à solution fraction (1107).
 *   1225, carte 2 « Quelle solution ? » (simple_solution_qcm_unique) :
 *     x + b = c à solution négative [−5 ; 5 ; 11 ; 3] ou ax = c négatif ;
 *     contenu.sousType = somme / produit.
 *   1226, carte 1 « Série flash » (etapes_flash_texte_trou_saisie) : quatre
 *     ax + b = c distinctes.
 *   1226, carte 2 « Quelle solution ? » (etapes_solution_qcm_unique) :
 *     ax + b = c [6 ; 12 ; 4 ; 20] (la clé encadrée) ; sousType deux_etapes.
 *   1226, carte 0 « Étape par étape » (etapes_guide_texte_trou_saisie) : la
 *     résolution écrite en cinq lignes, sept trous (4x + 5 − ⬚ = −19 − ⬚ ;
 *     4x = ⬚ ; 4x ÷ ⬚ = ⬚ ÷ ⬚ ; x = ⬚), forme ax ± b seulement ; a remplacé
 *     le glisser-déposer « Deux étapes » (trop compressé, prof 13/09).
 *
 * INVARIANTS : tout item vérifié par substitution (a × x + b = c, throw) ;
 * les solutions entières relatives, la fraction seulement dans ax = c ;
 * distracteurs distincts de la clé et entre eux.
 * SACRIFIÉS (fiches 13/09 : 1225 « Je défais l'opération » x + 8 = 3 et 3x = 12,
 * 1226 « Deux étapes » 3x + 2 = 14, 3x = 12, x = 4) : (8 ; 3) dans x + b = c,
 * (3x + 2 ; 14) sous toutes ses formes et lettres, (3 ; 12) dans ax = c,
 * plus (14 ; 32) et (4 ; 13) importés de 1107 ; auto-testés par
 * gen1225.self.test.js.
 *
 * ── Erreurs (familles nommées ; DEPP 2de 2025 q.6 « −2x = 1 », 44,4 %) ───
 *   operation_directe : c + b, a × c (1107), PRINCIPALE ;
 *   une_etape         : 2x + 4 = 16 → 12 (la division oubliée), NEUVE ;
 *   ordre_inverse     : 16 ÷ 2 − 4 = 4 (divisé avant de retirer), NEUVE ;
 *   signe_perdu       : x + 8 = 3 → 5 ; 3x = −12 → 4 (q.6 « divise par +2 »
 *                       20,5 %) ;
 *   lecture_additive  : 3x lu 3 + x (q.6 « ajoute 2 » 17,9 %) ;
 *   x_ignore          : le nombre de droite recopié.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeTexteTrou, tex,
} from '../../../utils';
import {
    tirerProduit, fr, fracDeuxCases, SACRIFIES as SACRIFIES_1107,
} from '../../4eme/calcul-litteral/gen_1107';

const ATTENDU_ID = 1225;
/** Une étape = 1225, deux étapes = 1226 (base du prof, 13/09). */
export const ATTENDU = { simple: 1225, etapes: 1226 };
const opts = (attendu, label) => ({ niveau: '3eme', matiere: 'maths', attendu, label });

const L_FLASH = 'Série flash';
const L_QCM = 'Quelle solution ?';
const L_GUIDE = 'Étape par étape';

const LETTRES = ['x', 'x', 'x', 'n', 't', 'y'];

// ─── Le domaine ───────────────────────────────────────────────────────────

/** Les exemples de la fiche : 3x + 2 = 14 (toutes formes) et 3x = 12 ; plus ceux de 1107 (importés). */
export const SACRIFIES = { deux_etapes: { a: 3, b: 2, c: 14 }, produit: { a: 3, c: 12 }, ...SACRIFIES_1107 };
/** L'exemple du signe de la fiche 1225 (coup de pouce 13/09) : x + 8 = 3, x = 3 - 8 = -5. */
export const SACRIFIE_SIGNE = { b: 8, c: 3 };

/** Un relatif en KaTeX : le signe moins est celui de KaTeX ; un négatif n'apparaît qu'après un signe égal, jamais après + ou −. */
const rel = (n) => String(n);

/**
 * Items : { type: 'somme', b, c, x, bDevant } (entiers relatifs, dixièmes
 * ×10 pour partager l'écriture de 1107) ; { type: 'produit', a, c, x }
 * (x entier relatif ou fraction de 1107) ; { type: 'deux_etapes', a, b,
 * signe, c, x, forme } avec forme ax_b / b_ax. Chaque item est vérifié.
 */
const sommeRelative = () => {
    for (let essai = 0; essai < 100; essai++) {
        const b = randInt(1, 20);
        const x = randInt(1, 4) === 1 ? -randInt(1, 15) : randInt(1, 25);
        const c = b + x;
        if (c === 0 || (b === SACRIFIES.somme.b && c === SACRIFIES.somme.c)) continue;
        if (b === SACRIFIE_SIGNE.b && c === SACRIFIE_SIGNE.c) continue;
        if (b + x !== c) throw new Error('gen_1225 : somme');
        return { type: 'somme', b: b * 10, c: c * 10, x: x * 10, bDevant: randomChoice([true, false]), relatif: true };
    }
    return { type: 'somme', b: 80, c: 30, x: -50, bDevant: false, relatif: true };
};
const produitEntier = ({ negatif = null } = {}) => {
    for (let essai = 0; essai < 100; essai++) {
        const a = randInt(2, 9);
        const neg = negatif ?? randInt(1, 3) === 1;
        const x = (neg ? -1 : 1) * randInt(2, 10);
        const c = a * x;
        if (SACRIFIES.produits.some((s) => s.a === a && s.c === c) || (a === SACRIFIES.produit.a && c === SACRIFIES.produit.c)) continue;
        if (a * x !== c) throw new Error('gen_1225 : produit');
        return { type: 'produit', a, c, x: { num: x, den: 1 } };
    }
    return { type: 'produit', a: 5, c: -20, x: { num: -4, den: 1 } };
};
const produitFraction = () => {
    for (let essai = 0; essai < 100; essai++) {
        const it = tirerProduit({ entier: false });
        if (it.x.den !== 1 && !(it.a === SACRIFIES.produit.a && it.c === SACRIFIES.produit.c)) return it;
    }
    return tirerProduit({ entier: false });
};
const deuxEtapes = ({ cMultiple = false } = {}) => {
    for (let essai = 0; essai < 200; essai++) {
        const a = randInt(2, 9);
        const b = randInt(1, 9);
        const signe = randomChoice(['+', '+', '-']);
        const x = randomChoice([-6, -5, -4, -3, -2, -1, 1, 2, 3, 4, 5, 6, 2, 3, 4, 5]);
        const c = a * x + (signe === '+' ? b : -b);
        if (c === 0) continue;
        if (cMultiple && c % a !== 0) continue;
        if (a === SACRIFIES.deux_etapes.a && b === SACRIFIES.deux_etapes.b && c === SACRIFIES.deux_etapes.c) continue;
        const forme = signe === '+' ? randomChoice(['ax_b', 'ax_b', 'b_ax']) : 'ax_b';
        if (a * x + (signe === '+' ? b : -b) !== c) throw new Error('gen_1225 : deux étapes');
        return { type: 'deux_etapes', a, b, signe, c, x, forme };
    }
    return { type: 'deux_etapes', a: 2, b: 4, signe: '+', c: 16, x: 6, forme: 'ax_b' };
};

// ─── Écritures ────────────────────────────────────────────────────────────

/** L'équation en KaTeX, avec la lettre. */
const equationTex = (it, L) => {
    if (it.type === 'somme') return it.bDevant ? `${fr(it.b)} + ${L} = ${rel(fr(it.c))}` : `${L} + ${fr(it.b)} = ${rel(fr(it.c))}`;
    if (it.type === 'produit') return `${it.a}${L} = ${rel(it.c)}`;
    if (it.forme === 'b_ax') return `${it.b} + ${it.a}${L} = ${rel(it.c)}`;
    return `${it.a}${L} ${it.signe} ${it.b} = ${rel(it.c)}`;
};
const solutionDe = (it) => (it.type === 'somme' ? fr(it.x) : it.type === 'produit' ? String(it.x.num) : String(it.x));
/** Le membre ax = … de la première étape. */
const premiereEtape = (it) => it.c - (it.signe === '+' ? it.b : -it.b);

const EXPLICATIONS = {
    operation_directe: 'Pour trouver x, on DÉFAIT l\'opération : x + 8 = 3, on retire 8 ; 3x = −12, on divise par 3.',
    une_etape: '2x + 4 = 16 : après avoir retiré 4, il reste 2x = 12, il faut encore diviser par 2 : x = 6.',
    ordre_inverse: 'On retire d\'abord le nombre seul, on divise ensuite : 2x + 4 = 16, 2x = 12, x = 6.',
    signe_perdu: 'x + 8 = 3 : x = 3 − 8 = −5, le signe compte. 3x = −12 : x = −12 ÷ 3 = −4.',
    lecture_additive: '3x, c\'est 3 × x, pas 3 + x.',
    x_ignore: 'Le nombre de droite n\'est pas la solution : il faut défaire l\'opération.',
    regle: 'x + b = c : x = c − b. ax = c : x = c ÷ a. ax + b = c : d\'abord ax = c − b, puis x = (c − b) ÷ a.',
};

// ─── Carte 1 : Série flash ────────────────────────────────────────────────

const CONSIGNE_FLASH = '<p><b>Résous chaque équation.</b></p>';

const ligneFlash = (it, L) => {
    const sol = it.type === 'produit' && it.x.den !== 1 ? fracDeuxCases(it.x) : `{{${solutionDe(it)}}}`;
    return `$${equationTex(it, L)} \\qquad ${L} = ${sol}$`;
};

/** 1225 : trois lignes (une somme, un produit entier, un produit à solution fraction) ; 1226 : quatre ax + b = c. */
const genFlash = (pour) => () => {
    for (let essai = 0; essai < 80; essai++) {
        const L = randomChoice(LETTRES);
        // 1225 : trois lignes (retour prof 13/09, le rappel sur a/b retiré) ; 1226 : quatre
        const items = pour === 'simple' ? [sommeRelative(), produitEntier(), produitFraction()] : [deuxEtapes(), deuxEtapes(), deuxEtapes(), deuxEtapes()];
        const cles = items.map((it) => equationTex(it, L));
        if (new Set(cles).size < items.length) continue;
        const payload = makeTexteTrou(shuffle(items).map((it) => ligneFlash(it, L)).join('\n'), CONSIGNE_FLASH, { ...opts(ATTENDU[pour], L_FLASH), mode: 'saisie', strict: true });
        payload.contenu = { ...payload.contenu, sousType: pour, explications: { regle: EXPLICATIONS.regle, signe_perdu: EXPLICATIONS.signe_perdu, une_etape: EXPLICATIONS.une_etape } };
        payload._meta.params = { pour, items, lettre: L };
        return payload;
    }
    throw new Error(`gen_1225 : série flash ${pour} sans tirage`);
};

// ─── Carte 2 : Quelle solution ? (QCM) ────────────────────────────────────

const genQcm = (pour) => () => {
    for (let essai = 0; essai < 100; essai++) {
        const L = randomChoice(LETTRES);
        const type = pour === 'etapes' ? 'deux_etapes' : randomChoice(['somme', 'produit']);
        let it; let faux;
        if (type === 'deux_etapes') {
            it = deuxEtapes({ cMultiple: true });
            const e1 = premiereEtape(it);
            faux = [
                { texte: String(e1), code: 'une_etape' },
                { texte: String(it.c / it.a - (it.signe === '+' ? it.b : -it.b)), code: 'ordre_inverse' },
                { texte: String(-it.x), code: 'signe_perdu' },
            ];
        } else if (type === 'somme') {
            it = sommeRelative();
            const [b, c, x] = [it.b / 10, it.c / 10, it.x / 10];
            if (x > 0 && randomChoice([true, false])) continue; // la solution négative surtout (le neuf)
            faux = [
                { texte: String(c + b), code: 'operation_directe' },
                { texte: String(-x), code: 'signe_perdu' },
                { texte: String(c), code: 'x_ignore' },
            ];
        } else {
            it = produitEntier({ negatif: true });
            faux = [
                { texte: String(-it.x.num), code: 'signe_perdu' },
                { texte: String(it.a * it.c), code: 'operation_directe' },
                { texte: String(it.c - it.a), code: 'lecture_additive' },
            ];
        }
        const bon = solutionDe(it);
        const choix = [bon, ...faux.map((f) => f.texte)];
        if (new Set(choix).size !== 4 || choix.includes('0')) continue;
        const payload = makeQcm(choix, bon, `<p><b>Quelle est la solution de ${tex(equationTex(it, L))} ?</b></p>`, opts(ATTENDU[pour], L_QCM));
        payload.contenu = { ...payload.contenu, sousType: type, explications: EXPLICATIONS };
        payload._meta.params = { ...it, pour, lettre: L, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    throw new Error(`gen_1225 : QCM ${pour} sans tirage`);
};

// ─── Carte « Étape par étape » (texte à trous en saisie, la résolution écrite) ──

/**
 * Demande prof 13/09 : « il faut un exercice beaucoup plus guidé, étape par
 * étape : on retire b de chaque membre, on divise chaque membre par a, on
 * trouve x ». La résolution est ÉCRITE EN ENTIER, ligne par ligne, l'élève
 * remplit les nombres :
 *   1226  4x + 5 = −19 / 4x + 5 − ⬚ = −19 − ⬚ / 4x = ⬚ / 4x ÷ ⬚ = ⬚ ÷ ⬚ / x = ⬚
 *         (7 trous ; ax − b = c : « + ⬚ » des deux côtés, on ajoute b) ;
 *   1225  x + 8 = 3 / x + 8 − ⬚ = 3 − ⬚ / x = ⬚  (3 trous) ou
 *         3x = −12 / 3x ÷ ⬚ = −12 ÷ ⬚ / x = ⬚  (3 trous, ou 4 avec la
 *         fraction en deux cases de 1107).
 * Lignes KaTeX pures (mode global), trous sans dollar ni accolade. Le −24
 * est retapé en ligne 4 : voulu, l'élève écrit « chaque membre ». Forme
 * ax + b seulement ici (b + ax reste en flash et QCM).
 */
const CONSIGNES_GUIDE = {
    somme: () => '<p><b>Résous étape par étape.</b> Retire le nombre seul de chaque membre.</p>',
    produit: (L) => `<p><b>Résous étape par étape.</b> Divise chaque membre par le nombre devant ${tex(L)}.</p>`,
    deux_etapes: (L) => `<p><b>Résous étape par étape.</b> Retire le nombre seul de chaque membre, puis divise chaque membre par le nombre devant ${tex(L)}.</p>`,
};

/** Les lignes de la résolution guidée (chaque ligne un $…$ complet). */
const lignesGuide = (it, L) => {
    const eq = equationTex(it, L);
    if (it.type === 'somme') {
        const b = fr(it.b);
        const gauche = it.bDevant ? `${b} + ${L}` : `${L} + ${b}`;
        return [`$${eq}$`, `$${gauche} - {{${b}}} = ${rel(fr(it.c))} - {{${b}}}$`, `$${L} = {{${fr(it.x)}}}$`];
    }
    if (it.type === 'produit') {
        const sol = it.x.den !== 1 ? fracDeuxCases(it.x) : `{{${it.x.num}}}`;
        return [`$${eq}$`, `$${it.a}${L} \\div {{${it.a}}} = ${rel(it.c)} \\div {{${it.a}}}$`, `$${L} = ${sol}$`];
    }
    const e1 = premiereEtape(it);
    const defaire = it.signe === '+' ? `- {{${it.b}}}` : `+ {{${it.b}}}`;
    return [
        `$${eq}$`,
        `$${it.a}${L} ${it.signe} ${it.b} ${defaire} = ${rel(it.c)} ${defaire}$`,
        `$${it.a}${L} = {{${e1}}}$`,
        `$${it.a}${L} \\div {{${it.a}}} = {{${e1}}} \\div {{${it.a}}}$`,
        `$${L} = {{${it.x}}}$`,
    ];
};

/** 1225 : une somme (une fois sur deux) ou un produit (entier deux fois sur trois) ; 1226 : ax ± b = c. */
const genGuide = (pour) => () => {
    const L = randomChoice(LETTRES);
    let it;
    if (pour === 'simple') it = randomChoice([sommeRelative, sommeRelative, produitEntier, produitEntier, produitFraction])();
    else { it = deuxEtapes(); it.forme = 'ax_b'; }
    const payload = makeTexteTrou(lignesGuide(it, L).join('\n'), CONSIGNES_GUIDE[it.type](L), { ...opts(ATTENDU[pour], L_GUIDE), mode: 'saisie', strict: true });
    payload.contenu = { ...payload.contenu, sousType: it.type, explications: { regle: EXPLICATIONS.regle, ordre_inverse: EXPLICATIONS.ordre_inverse, signe_perdu: EXPLICATIONS.signe_perdu } };
    payload._meta.params = { ...it, pour, lettre: L };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'un fichier, deux attendus : clés simple_* (1225, une étape) et etapes_* (1226, deux étapes) ; quatre choix distincts, jamais 0 ; deux étapes : c multiple de a pour que ordre_inverse soit entier ; contenu.sousType = deux_etapes / somme / produit au QCM',
        'sacrifiés : (3x + 2 ; 14) toutes formes et lettres, (3 ; 12) dans ax = c, (14 ; 32) et (4 ; 13) importés de 1107',
    ],
    solution_qcm_unique: {
        familles: {
            une_etape: 'c − b : la division oubliée (2x + 4 = 16 → 12), la principale en deux étapes',
            ordre_inverse: 'c ÷ a − b : divisé avant de retirer (→ 4)',
            signe_perdu: '−x : le signe perdu (x + 8 = 3 → 5 ; 3x = −12 → 4 ; DEPP 2de 2025 q.6, 20,5 %)',
            operation_directe: 'c + b ; a × c (1107)',
            lecture_additive: 'c − a : 3x lu 3 + x (q.6, 17,9 %)',
            x_ignore: 'c recopié',
        },
    },
    guide_texte_trou_saisie: 'saisie, pas de leurre : la résolution écrite en entier, l\'élève remplit les nombres (b deux fois, c ∓ b, a deux fois, c ∓ b retapé, x)',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    simple_guide_texte_trou_saisie: genGuide('simple'),
    simple_flash_texte_trou_saisie: genFlash('simple'),
    simple_solution_qcm_unique: genQcm('simple'),
    etapes_guide_texte_trou_saisie: genGuide('etapes'),
    etapes_flash_texte_trou_saisie: genFlash('etapes'),
    etapes_solution_qcm_unique: genQcm('etapes'),
};

/** 1225 : une étape. */
export const CATEGORIES = [
    { id: 'simple_guide', label: L_GUIDE, etapes: 1, formatsList: [{ key: 'simple_guide_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'simple_flash', label: L_FLASH, etapes: 1, formatsList: [{ key: 'simple_flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'simple_solution', label: L_QCM, etapes: 1, formatsList: [{ key: 'simple_solution_qcm_unique', label: 'QCM' }] },
];
/** 1226 : deux étapes (gen_1226). */
export const CATEGORIES_ETAPES = [
    { id: 'etapes_guide', label: L_GUIDE, etapes: 2, formatsList: [{ key: 'etapes_guide_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'etapes_flash', label: L_FLASH, etapes: 2, formatsList: [{ key: 'etapes_flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'etapes_solution', label: L_QCM, etapes: 2, formatsList: [{ key: 'etapes_solution_qcm_unique', label: 'QCM' }] },
];
const clesDe = (cats) => cats.flatMap((c) => c.formatsList.map(({ key }) => key));

/** Tire parmi les clés données (dispatch des deux gens). */
export const genererParmi = (cles, typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = cles.filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(cles);
    } else key = randomChoice(cles);
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const generate = (typeExercice) => genererParmi(clesDe(CATEGORIES), typeExercice);
export { ATTENDU_ID };

